import { Transition } from '@headlessui/react';
import classNames from 'classnames';
import { Fragment, memo, ReactNode, RefObject, useCallback, useRef } from 'react';

import { IconSvg } from '@/lib/v2/components';
import { CloseIcon } from '@/lib/v2/icons/outline';

interface SidePanelProps {
  children: ReactNode;
  isOpen: boolean;
  onClose?: (value?: boolean) => void;
  withOutExternalClose?: boolean;
  id?: string;
  mountTransitionEnd?: (refContainerSidePanel?: RefObject<HTMLDivElement>) => void;
  withCloseButton?: boolean;
  noPadding?: boolean;
}

const SidePanel = ({
  children,
  isOpen,
  onClose,
  withOutExternalClose = false,
  id,
  mountTransitionEnd,
  withCloseButton = false,
  noPadding = false,
}: SidePanelProps) => {
  const refSidePanel = useRef<HTMLDivElement>(null);

  const classesContainer = classNames(
    'transform bg-white shadow-xl transition-all h-[calc(100vh-64px)] w-full max-w-[550px]',
    {
      'p-6': !noPadding,
    }
  );

  const classesClose = classNames(
    'absolute z-10 rounded-xl bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emblue-primary focus:ring-offset-2 p-1'
  );

  const handleAfterEnter = useCallback(() => {
    mountTransitionEnd?.(refSidePanel);
  }, [mountTransitionEnd]);

  const handleAfterLeave = useCallback(() => {
    onClose?.(false);
  }, [onClose]);

  const handleClose = useCallback(() => {
    onClose?.(false);
  }, [onClose]);

  return (
    <Transition
      appear
      afterEnter={handleAfterEnter}
      afterLeave={handleAfterLeave}
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0 translate-x-full"
      enterTo="opacity-100 translate-x-0"
      leave="ease-in duration-300"
      leaveFrom="opacity-100 translate-x-0"
      leaveTo="opacity-0 translate-x-full"
      show={isOpen}
    >
      <div className="relative z-[999999]" data-testid="side-panel-component" id={id}>
        <div className="fixed inset-0 bg-black/25" />
        <div
          aria-hidden
          className="fixed inset-0 z-[999999] overflow-hidden"
          onClick={(e) => {
            if (!withOutExternalClose && (e.target as HTMLElement).id === 'side-panel-container') {
              handleClose();
            }
          }}
        >
          <div className={'flex min-h-full items-end justify-end'} id="side-panel-container">
            <Transition.Child
              as={Fragment}
              enter={'ease-out duration-300 transform translate-x-full'}
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-300"
              leaveFrom="opacity-100"
              leaveTo={'opacity-0 transform translate-x-full'}
            >
              <div
                ref={refSidePanel}
                aria-hidden
                className={classesContainer}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative flex size-full flex-col">
                  {withCloseButton && (
                    <button className={classesClose} type="button" onClick={handleClose}>
                      <span className="sr-only">Close</span>
                      <IconSvg svgComponent={<CloseIcon />} />
                    </button>
                  )}

                  <div className="relative size-full">{children}</div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default memo(SidePanel);
